/**
 * Subject display names for the UI.
 *
 * `Subject.name` ('maths', 'physics') is the INTERNAL key — it keys API params,
 * query keys, CSS tint tokens and stored question fields. It must never reach
 * the screen. The catalog's `displayName` ('Mathematics') is the only thing a
 * student or teacher should ever read, so every label goes through here.
 *
 * `useSubjectLabel` is the reactive form and the default choice: it re-renders
 * when `/subjects` resolves. `subjectDisplayName` is the sync escape hatch for
 * helpers that run inside a render loop where a hook can't be called; it reads
 * the same localStorage catalog cache `useSubjects` writes. Both capitalize the
 * internal name when the cache is cold, so a first paint degrades to "Maths"
 * rather than a bare lowercase key.
 */
import { useCallback } from 'react';
import { isExtendedSubject, extendedSubjectLabel, titleCaseSubjectKey } from '@padcanvas/shared';
import { useSubjects, getCachedSubjects } from './useSubjects';
import type { SubjectBase } from './types';

/**
 * What to show for a subject the catalog does not carry.
 *
 * The EXTENDED subjects (History, Geography, English Grammar) are deliberately
 * absent from `/subjects`, and their labels have lived in `academicTypes` all
 * along — this resolver simply never asked, so every surface using it rendered
 * the raw key and `english_grammar` reached the screen as "English_grammar".
 *
 * Anything else title-cases, treating `_`/`-` as word breaks: a separator left
 * in place announces the key as a key.
 */
const capitalize = (s: string) => {
    if (!s) return '';
    return isExtendedSubject(s) ? extendedSubjectLabel(s) : titleCaseSubjectKey(s);
};

/** Resolve against an already-loaded catalog. Accepts an internal name or a subject id. */
export function resolveSubjectLabel(subject: string | undefined, catalog: SubjectBase[]): string {
    if (!subject) return '';
    const lc = subject.toLowerCase();
    const hit = catalog.find((s) => s.name?.toLowerCase() === lc || s.id === subject);
    return hit?.displayName || capitalize(subject);
}

/** Non-hook read of the cached catalog. Prefer `useSubjectLabel` in components. */
export function subjectDisplayName(subject?: string): string {
    return resolveSubjectLabel(subject, getCachedSubjects());
}

/** Reactive resolver — re-renders once the subject catalog loads. */
export function useSubjectLabel(): (subject?: string) => string {
    const { subjects } = useSubjects<SubjectBase>();
    return useCallback((subject?: string) => resolveSubjectLabel(subject, subjects), [subjects]);
}

/**
 * A label for EVERY subject something covers.
 *
 * A question set's `subjects` is an array, and for a JEE or NEET mock it holds
 * three. Rendering `subjects[0]` labels such a paper "Physics" — not a summary
 * but a wrong answer, and one nobody can tell from a right one, since the array
 * order is whatever the derivation happened to produce.
 *
 * Past `max` the list is counted rather than spelled out: the surfaces that show
 * this are cards and one-line metadata, where three full subject names crowd out
 * the title they sit under. `max` is per call site — a roomy banner can afford
 * to name all three, a compact card cannot.
 */
export function resolveSubjectsLabel(
    subjects: readonly string[] | undefined,
    catalog: SubjectBase[],
    max = 2,
): string {
    const list = (subjects ?? []).filter(Boolean);
    if (list.length === 0) return '';
    if (list.length > max) return `${list.length} subjects`;
    const labels = list.map((s) => resolveSubjectLabel(s, catalog));
    if (labels.length === 1) return labels[0];
    return `${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
}

/** Non-hook read of the cached catalog. Prefer `useSubjectsLabel` in components. */
export function subjectsDisplayName(subjects?: readonly string[], max?: number): string {
    return resolveSubjectsLabel(subjects, getCachedSubjects(), max);
}

/** Reactive plural resolver — the list form of `useSubjectLabel`. */
export function useSubjectsLabel(): (subjects?: readonly string[], max?: number) => string {
    const { subjects: catalog } = useSubjects<SubjectBase>();
    return useCallback(
        (subjects?: readonly string[], max?: number) => resolveSubjectsLabel(subjects, catalog, max),
        [catalog],
    );
}
